// ActionEventsTab — list of events linked to the current action, with link/unlink.
// Data comes from GET /actions/{action_id}/events.
// Actions own this relationship via action_links table (linked_to_type='event').

import { useState, useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { actionsApi, type LinkedActionEvent } from '@/lib/api/actions'
import {
  eventsApi,
  type Event,
  eventLifecycleStateLabels,
  eventLifecycleStateColors,
} from '@/lib/api/events'
import { categoriesApi } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { LinkPickerDialog, type PickerTreeNode } from '@/components/shared/LinkPickerDialog'
import { ConfirmUnlinkDialog } from '@/components/shared/ConfirmUnlinkDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Zap,
  FolderOpen,
  Calendar,
  ExternalLink,
  Plus,
  Unlink,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export function ActionEventsTab() {
  const { slug } = useTenant()
  const { actionId } = useParams({ strict: false }) as { actionId: string }
  const navigate = useNavigate()
  const api = useApi()
  const queryClient = useQueryClient()
  const actApi = actionsApi(api)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedActionEvent | null>(null)

  // Linked events for this action
  const {
    data: events,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.actions.events(slug, actionId),
    queryFn: () => actApi.events(actionId),
  })

  // Event category tree for the picker — fetched when picker opens
  const { data: categoryTree, isLoading: categoriesLoading } = useQuery({
    queryKey: queryKeys.categories.tree(slug, 'event'),
    queryFn: () => categoriesApi(api, 'event').tree(),
    enabled: pickerOpen,
  })

  // All events (permission-filtered) for the picker
  const { data: allEvents, isLoading: eventsLoading } = useQuery({
    queryKey: queryKeys.events.all(slug),
    queryFn: () => eventsApi(api).list(),
    enabled: pickerOpen,
  })

  const existingIds = useMemo(
    () => new Set(events?.map((e) => e.event_id) ?? []),
    [events],
  )

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: ({ linkId, notes }: { linkId: string; notes: string }) =>
      actApi.unlinkEvent(actionId, linkId, notes || undefined),
    onSuccess: () => {
      setUnlinkTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.events(slug, actionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked events"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header with count and add button */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {events?.length ?? 0} linked event{events?.length !== 1 ? 's' : ''}
        </div>
        <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Link Event
        </Button>
      </div>

      {/* Empty state */}
      {(!events || events.length === 0) && (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <Zap className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">No linked events.</p>
            <p className="text-xs text-muted-foreground/60">
              Use the button above to link events to this action.
            </p>
          </div>
        </div>
      )}

      {/* Event list */}
      {events && events.length > 0 && (
        <div className="divide-y rounded-lg border">
          {events.map((event) => (
            <EventRow
              key={event.link_id}
              event={event}
              onClick={() => navigate({ to: `/t/${slug}/events/${event.event_id}` })}
              onUnlink={() => setUnlinkTarget(event)}
            />
          ))}
        </div>
      )}

      {/* Link picker dialog */}
      <LinkPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onLinked={() => {
          setPickerOpen(false)
          queryClient.invalidateQueries({ queryKey: queryKeys.actions.events(slug, actionId) })
          queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
        }}
        title="Link Event"
        description="Select an event to link to this action."
        treeNodes={categoryTree as PickerTreeNode[] | undefined}
        objects={allEvents}
        isLoading={eventsLoading || categoriesLoading}
        existingIds={existingIds}
        getObjectId={(e: Event) => e.id}
        getObjectParentId={(e: Event) => e.category_id}
        getObjectSearchText={(e: Event) => `${e.title} ${e.event_type_name} ${e.category_name}`}
        renderObject={(e: Event, isSelected: boolean) => (
          <EventPickerRow event={e} isSelected={isSelected} />
        )}
        linkFn={(eventId: string) => actApi.linkEvent(actionId, eventId)}
        linkButtonLabel="Link Event"
        searchPlaceholder="Search events or categories..."
      />

      {/* Unlink confirmation dialog */}
      {unlinkTarget && (
        <ConfirmUnlinkDialog
          open={!!unlinkTarget}
          onClose={() => setUnlinkTarget(null)}
          onConfirm={(notes) =>
            unlinkMutation.mutate({ linkId: unlinkTarget.link_id, notes })
          }
          title="Remove Event Link"
          description={`Remove the link between this action and "${unlinkTarget.event_title}"?`}
          isPending={unlinkMutation.isPending}
        />
      )}
    </div>
  )
}

function EventRow({
  event,
  onClick,
  onUnlink,
}: {
  event: LinkedActionEvent
  onClick: () => void
  onUnlink: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Title */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{event.event_title}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      {/* Category */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{event.category_name}</span>
      </div>

      {/* Linked date */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
        <Calendar className="h-3 w-3" />
        <span>{new Date(event.linked_at).toLocaleDateString()}</span>
      </div>

      {/* Status badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          eventLifecycleStateColors[event.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {eventLifecycleStateLabels[event.lifecycle_state] ?? event.lifecycle_state}
      </Badge>

      {/* Unlink button */}
      <button
        className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onUnlink()
        }}
        title="Remove link"
      >
        <Unlink className="h-4 w-4" />
      </button>
    </div>
  )
}

function EventPickerRow({
  event,
  isSelected,
}: {
  event: Event
  isSelected: boolean
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={cn('truncate', isSelected && 'font-medium')}>{event.title}</span>
      <span className="text-xs text-muted-foreground">{event.event_type_name}</span>
      <Badge
        variant="outline"
        className={cn(
          'ml-auto flex-shrink-0 px-1.5 py-0 text-xs',
          eventLifecycleStateColors[event.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {eventLifecycleStateLabels[event.lifecycle_state] ?? event.lifecycle_state}
      </Badge>
    </div>
  )
}
